// ─── Hero.jsx — config-driven version ────────────────────────────────────────
//
// Reads all text, video URLs, blob sizes/colors, and animation values
// from Firestore (heroConfig/main) on mount.
// Falls back to DEFAULTS when the document doesn't exist yet.
// All keys match 1:1 with HeroControlPage.jsx.
// ─────────────────────────────────────────────────────────────────────────────

import { useEffect, useRef, useState } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { Observer } from 'gsap/Observer';

gsap.registerPlugin(ScrollTrigger, Observer);

// ── Firestore REST ────────────────────────────────────────────────────────────
const PROJECT_ID = 'portfolio-e79b8-5854c';
const API_KEY    = 'AIzaSyDLsmnqt6wth0b3hZYrnllXnJIcjUaK8Uc';
const REST_BASE  = `https://firestore.googleapis.com/v1/projects/${PROJECT_ID}/databases/(default)/documents`;

function fromValue(v) {
  if (!v) return null;
  if ('stringValue'  in v) return v.stringValue;
  if ('integerValue' in v) return Number(v.integerValue);
  if ('doubleValue'  in v) return Number(v.doubleValue);
  if ('booleanValue' in v) return v.booleanValue;
  if ('nullValue'    in v) return null;
  if ('arrayValue'   in v) return (v.arrayValue.values || []).map(fromValue);
  if ('mapValue'     in v) return fromFields(v.mapValue.fields || {});
  return null;
}
function fromFields(fields) {
  const obj = {};
  for (const [k, v] of Object.entries(fields)) obj[k] = fromValue(v);
  return obj;
}

async function fetchHeroConfig() {
  try {
    const res = await fetch(`${REST_BASE}/heroConfig/main?key=${API_KEY}`);
    if (res.status === 404) return null;
    if (!res.ok) return null;
    const json = await res.json();
    return fromFields(json.fields || {});
  } catch {
    return null;
  }
}

// ── Defaults — identical to HeroControlPage DEFAULTS ─────────────────────────
const DEFAULTS = {
  headlineText:            'Naveen Ak Fullstack Developer',
  scrollIndicatorText:     'SCROLL DOWN',
  footerText:              'To Explore',
  cursorText:              'scroll down ↓',

  headlineFontSize_desktop: 10.5,
  headlineFontSize_mobile:  11,
  headlineColor:            'var(--white)',
  headlineLetterSpacing:    -3,
  headlineLineHeight:       105,

  videoDesktopUrl:         './des.mp4',
  videoMobileUrl:          './vid_m.mp4',
  videoObjectPosition:     'center center',

  revealScrub:             1.4,
  revealPinLength:         200,
  revealScaleStart:        0,
  revealScaleEnd:          1,
  revealBorderRadiusStart: 50,
  revealBorderRadiusEnd:   0,
  revealDuration:          0.7,
  revealEase:              'power2.inOut',

  scrollIndDelay:          0.5,
  scrollIndDuration:       0.8,

  blob1ColorStart:         'rgba(200,240,74,0.07)',
  blob1Top:                5,
  blob1Right:              10,
  blob1Size_desktop:       500,
  blob1Size_mobile:        260,
  blob1Blur:               60,

  blob2ColorStart:         'rgba(0,229,255,0.04)',
  blob2Bottom:             10,
  blob2Left:               5,
  blob2Size_desktop:       350,
  blob2Size_mobile:        200,
  blob2Blur:               80,

  gridEnabled:             true,
  gridOpacity:             3,
  gridSize:                55,
  gridColor:               'var(--acid)',

  blobPulseEnabled:        true,
  blobPulseMax:            3,
  blobPulseStrength:       0.06,
  blobPulseDuration:       0.5,
};

export default function Hero({ loaded, isMobile }) {
  const heroRef = useRef(null);
  const [cfg, setCfg] = useState(null); // null while loading

  // ── Fetch config ───────────────────────────────────────────────────────────
  useEffect(() => {
    fetchHeroConfig()
      .then(remote => setCfg(remote ? { ...DEFAULTS, ...remote } : { ...DEFAULTS }));
  }, []);

  // ── Run GSAP only after both loaded + config ready ─────────────────────────
  useEffect(() => {
    if (!loaded || !cfg) return;

    const c = cfg; // stable snapshot for closure

    const ctx = gsap.context(() => {

      // ── Photo/video reveal on scroll ──
      gsap.timeline({
        scrollTrigger: {
          trigger: heroRef.current,
          start: 'top top',
          end: `+=${c.revealPinLength}%`,
          scrub: c.revealScrub,
          pin: true, pinSpacing: true, anticipatePin: 1,
        },
      }).fromTo('.hero-photo-wrap',
        {
          scale: c.revealScaleStart,
          opacity: 1,
          borderRadius: `${c.revealBorderRadiusStart}%`,
        },
        {
          scale: c.revealScaleEnd,
          opacity: 1,
          borderRadius: `${c.revealBorderRadiusEnd}%`,
          ease: c.revealEase,
          duration: c.revealDuration,
        }
      );

      // ── Scroll indicator fade-in ──
      gsap.fromTo('.hero-scroll-ind',
        { opacity: 0, y: 10 },
        {
          opacity: 1, y: 0,
          duration: c.scrollIndDuration,
          delay: c.scrollIndDelay,
          ease: 'power2.out',
        }
      );

      // ── Blob scroll pulse ──
      if (c.blobPulseEnabled) {
        Observer.create({
          target: window, type: 'scroll',
          onChange: (self) => {
            const vel = Math.min(Math.abs(self.velocityY) * 0.001, c.blobPulseMax);
            gsap.to('.hero-blob', {
              scale: 1 + vel * c.blobPulseStrength,
              duration: c.blobPulseDuration,
              ease: 'power2.out',
              overwrite: 'auto',
            });
          },
        });
      }

    }, heroRef);

    return () => ctx.revert();
  }, [loaded, cfg]);

  // Render with defaults while config loads (avoids blank flash)
  const c = cfg ?? DEFAULTS;

  const blob1Size = isMobile ? c.blob1Size_mobile : c.blob1Size_desktop;
  const blob2Size = isMobile ? c.blob2Size_mobile : c.blob2Size_desktop;
  const headlineFsMax = isMobile ? c.headlineFontSize_mobile : c.headlineFontSize_desktop;
  const headlineFsMin = isMobile ? '3.2rem' : '3rem';
  const headlineFsFixed = isMobile ? '4rem' : '10rem';
  const videoSrc = isMobile ? c.videoMobileUrl : c.videoDesktopUrl;

  return (
    <section
      id="about"
      ref={heroRef}
      data-cursor-text={c.cursorText}
      style={{
        height: '100vh', background: 'var(--black)',
        position: 'relative', overflow: 'hidden',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}
    >
      {/* Grid bg */}
      {c.gridEnabled && (
        <div style={{
          position: 'absolute', inset: 0,
          opacity: (c.gridOpacity || 3) / 100,
          backgroundImage: `linear-gradient(${c.gridColor} 1px,transparent 1px),linear-gradient(90deg,${c.gridColor} 1px,transparent 1px)`,
          backgroundSize: `${c.gridSize}px ${c.gridSize}px`,
          pointerEvents: 'none',
        }} />
      )}

      {/* Blob 1 */}

      {/* Blob 2 */}
      <div className="hero-blob" style={{
        position: 'absolute',
        bottom: `${c.blob2Bottom}%`,
        left: `${c.blob2Left}%`,
        width: blob2Size,
        height: blob2Size,
        background: `radial-gradient(circle,${c.blob2ColorStart} 0%,transparent 65%)`,
        borderRadius: '50%',
        filter: `blur(${c.blob2Blur}px)`,
        pointerEvents: 'none',
      }} />

      {/* Hero headline */}
      <span style={{
        fontFamily: 'Syne', fontWeight: 800,
        fontSize: `clamp(${headlineFsMin}, ${headlineFsMax}vw, ${headlineFsFixed})`,
        letterSpacing: `${(c.headlineLetterSpacing || -3) / 100}em`,
        lineHeight: (c.headlineLineHeight || 105) / 100,
        color: c.headlineColor || 'var(--white)',
        userSelect: 'none', zIndex: 3,
        position: 'relative', width: '94vw',
        textAlign: 'left', display: 'block',
        wordBreak: 'break-word',
      }}>
        {c.headlineText}
      </span>

      {/* Video reveal */}
      <div className="hero-photo-wrap" style={{
        position: 'absolute', top: 0, left: 0,
        width: '100%', height: '100%', zIndex: 4,
        overflow: 'hidden', opacity: 0,
        transform: `scale(${c.revealScaleStart})`,
        borderRadius: `${c.revealBorderRadiusStart}%`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: 'black',
      }}>
        <video
        key={videoSrc}   
          autoPlay muted loop playsInline
          style={{
            width: '100%', height: '100%',
            objectFit: 'cover',
            objectPosition: c.videoObjectPosition || 'center center',
          }}
          onError={e => {
            e.currentTarget.style.display = 'none';
            e.currentTarget.parentElement.style.background = 'black';
          }}
        >
          <source src={videoSrc} type="video/mp4" />
        </video>
      </div>

      {/* Scroll indicator */}
      <div className="hero-scroll-ind" style={{
        position: 'absolute',
        bottom: isMobile ? '1.5rem' : '2.5rem',
        left: '50%', transform: 'translateX(-50%)',
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        gap: isMobile ? '0.5rem' : '0.75rem',
        fontFamily: 'Space Mono', fontSize: isMobile ? '0.5rem' : '0.6rem',
        color: 'var(--muted)', letterSpacing: '0.15em', zIndex: 6, opacity: 0,
      }}>
        {c.scrollIndicatorText}
        <div style={{
          width: 1, height: isMobile ? 40 : 60,
          background: 'linear-gradient(to bottom,transparent,var(--acid))',
          animation: 'scrollPulse 2s infinite',
        }} />
      </div>

      {/* Footer */}
      <div style={{
        position: 'absolute', bottom: '0.8rem', left: '50%',
        transform: 'translateX(-50%)',
        fontFamily: 'Space Mono', fontSize: '0.5rem', color: 'var(--muted)',
        letterSpacing: '0.2em', zIndex: 6, whiteSpace: 'nowrap',
      }}>
        {c.footerText}
      </div>
    </section>
  );
}